import { useEffect, useState } from 'react';
import { disableWebPush, enableWebPush, isSubscribed, pushCapability } from '../lib/webPush';
import { colors } from '../theme';

// Shown in the group picker sheet when Web Push is configured and the
// environment is capable. Hidden entirely if VITE_VAPID_PUBLIC_KEY isn't set
// — testers shouldn't see a broken toggle before the backend is deployed.

export function NotificationsToggle({ userId }: { userId: string }) {
  const [cap, setCap] = useState(pushCapability());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    isSubscribed().then(setSubscribed);
  }, []);

  // Re-evaluate capability if the user A2HSes mid-session and revisits.
  useEffect(() => {
    const onVis = () => setCap(pushCapability());
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  if (cap === 'no-vapid' || cap === 'unsupported') return null;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      if (subscribed) {
        await disableWebPush(userId);
        setSubscribed(false);
      } else {
        await enableWebPush(userId);
        setSubscribed(true);
      }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="notif-wrap">
      <div className="notif-row">
        <div>
          <div className="notif-title">Urgent notifications</div>
          <div className="notif-sub">
            {cap === 'needs-install'
              ? 'Install the app to enable push alerts.'
              : cap === 'needs-permission'
              ? 'Tap to grant notification permission.'
              : subscribed
              ? 'On — urgent messages will alert you even when the app is closed.'
              : 'Off — tap to enable.'}
          </div>
        </div>
        <button
          onClick={toggle}
          disabled={busy || cap === 'needs-install'}
          className={subscribed ? 'notif-btn notif-on' : 'notif-btn'}
        >
          {busy ? '…' : subscribed ? 'On' : 'Enable'}
        </button>
      </div>
      {error && <div className="notif-error">{error}</div>}

      <style>{`
        .notif-wrap {
          margin: 12px 16px;
          padding: 14px;
          background: ${colors.surface};
          border-radius: 10px;
          border: 1px solid ${colors.divider};
        }
        .notif-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .notif-title { color: ${colors.text}; font-size: 14px; font-weight: 600; }
        .notif-sub { color: ${colors.textMuted}; font-size: 12px; margin-top: 2px; line-height: 1.4; }
        .notif-btn {
          background: transparent;
          color: ${colors.accent};
          border: 1px solid ${colors.accent};
          border-radius: 8px;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 700;
        }
        .notif-btn:disabled { opacity: 0.4; }
        .notif-on {
          background: ${colors.accent};
          color: ${colors.bg};
        }
        .notif-error {
          margin-top: 10px;
          color: ${colors.error};
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}
